import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, Badge, Button } from '@/components/ui'
import { useAuthStore } from '@/store'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import {
  getDocumentsRegistryRow,
  upsertStudioDocument,
  deleteStudioDocument,
  DOCUMENT_TYPE_LABELS,
  DOC_READINESS_LABELS,
} from '@/services/documentsRegistryService'
import { ContractEditor } from '@/components/contract/ContractEditor'
import type {
  StudioDocumentsRegistryRow,
  StudioDocument,
  DocumentType,
  DocReadiness,
} from '@/types'
import { ArrowLeft, AlertTriangle, ExternalLink, FileSignature, Save, Trash2 } from 'lucide-react'
import { LegalNameChangeModal } from '@/components/identity/LegalNameChangeModal'

const inputCls = 'w-full px-3 py-2 border border-border rounded-md bg-background text-sm'
const legalErrorAlertCls =
  'flex items-start gap-2 rounded-md border-2 border-destructive bg-destructive/15 px-3 py-2.5 text-sm font-medium text-destructive shadow-sm'
const legalErrorIconCls = 'mt-0.5 flex-none'

function LegalErrorAlert({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div role="alert" aria-live="assertive" className={`${legalErrorAlertCls} ${className}`}>
      <AlertTriangle size={16} className={legalErrorIconCls} />
      <span className="min-w-0 break-words">{children}</span>
    </div>
  )
}

// В карточке намеренно показывается ТОЛЬКО папка франчайзи. Остальные типы из `DocumentType`
// (ПДКК, заверение ГК, НДА, ДС Роспатент, согласие ОПД, ДКП, ДС/уведомления-папки, расторжение,
// прочее) пока СКРЫТЫ и, возможно, будут удалены из модели — не выводить их здесь.
const VISIBLE_DOCUMENT_TYPES: DocumentType[] = ['PARTNER_FOLDER']

function fmtDate(value: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('ru-RU')
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm">{value ?? '—'}</div>
    </div>
  )
}

export function DocumentsRegistryDetailPage() {
  const navigate = useNavigate()
  const { contractId = '' } = useParams<{ contractId: string }>()
  const cid = Number(contractId)
  const { user } = useAuthStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)

  const [row, setRow] = useState<StudioDocumentsRegistryRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isAdvanced, setIsAdvanced] = useState(false)
  const [legalNameOpen, setLegalNameOpen] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    getDocumentsRegistryRow(cid)
      .then(data => {
        setRow(data)
        setError(data ? null : 'ДКК не найден')
      })
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [cid])

  useEffect(() => {
    if (!Number.isFinite(cid)) {
      setError('Некорректный ДКК')
      setLoading(false)
      return
    }
    load()
  }, [cid, load])

  useEffect(() => {
    let cancelled = false
    if (!user?.id) return
    getAccessLevel().then(level => {
      if (!cancelled) setIsAdvanced(level === 'advanced')
    })
    return () => {
      cancelled = true
    }
  }, [getAccessLevel, user?.id])

  const docsByType = useMemo(() => {
    const map = new Map<DocumentType, StudioDocument>()
    row?.documents?.forEach(d => map.set(d.doc_type, d))
    return map
  }, [row])

  if (loading) return <div className="p-4 text-sm text-muted-foreground">Загрузка…</div>

  if (error && !row) {
    return (
      <div className="space-y-4 pb-24">
        <Button variant="outline" size="sm" onClick={() => navigate('/management/documents-registry')}>
          <ArrowLeft size={16} className="mr-1" /> Назад
        </Button>
        <LegalErrorAlert>{error}</LegalErrorAlert>
      </div>
    )
  }

  if (!row) return null

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" size="sm" onClick={() => navigate('/management/documents-registry')}>
          <ArrowLeft size={16} className="mr-1" /> Реестр
        </Button>
        <div className="text-sm font-semibold">
          ДКК {row.contract_number ?? '—'} · студия {row.studio_number ?? '—'}
        </div>
      </div>

      {error && (
        <LegalErrorAlert>{error}</LegalErrorAlert>
      )}

      {/* 1. Договор как сущность: статус, даты, ДС и условия роялти (общий редактор с роялти) */}
      <ContractEditor contractId={cid} onSaved={load} />

      {/* 2. Документы */}
      <Card>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="text-sm font-semibold">Документы</div>
        </div>
        <div className="text-xs text-muted-foreground mb-3">
          Ссылки и статусы папки франчайзи.
        </div>

        {row.documents_total > 0 && (
          <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
            <span className="whitespace-nowrap">
              Готово {row.documents_ready}/{row.documents_total}
            </span>
            <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary rounded-full"
                style={{ width: `${Math.round((row.documents_ready / row.documents_total) * 100)}%` }}
              />
            </div>
          </div>
        )}

        <div className="space-y-2">
          {VISIBLE_DOCUMENT_TYPES.map(type => (
            <DocumentRow
              key={type}
              contractId={cid}
              docType={type}
              doc={docsByType.get(type) ?? null}
              isAdvanced={isAdvanced}
              onChanged={load}
              onError={setError}
            />
          ))}
        </div>
      </Card>

      {/* 3. Мастер-данные — только чтение, правятся в своих экранах.
           Исключение: юридическое ФИО и названия юрлиц. Это документные реквизиты
           стороны, поэтому меняются здесь, у юристов, а не на «Оригиналах»
           (docs/PARTY_IDENTITY_CHANGE_PLAN.md). */}
      <Card>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div>
            <div className="text-sm font-semibold">Франчайзи и студия</div>
            <div className="text-xs text-muted-foreground">
              Только для чтения. Правятся на экранах франчайзи и студии — кроме ФИО стороны и её юрлиц.
            </div>
          </div>
          {isAdvanced && row.partner_number != null && (
            <Button variant="outline" size="sm" onClick={() => setLegalNameOpen(true)}>
              <FileSignature size={14} className="mr-1.5" />
              Смена ФИО
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
          <Field label="Франчайзи" value={row.partner_fio ?? `№ ${row.partner_number ?? '—'}`} />
          <Field label="Город" value={row.city_name} />
          <Field label="Статус студии" value={row.studio_status} />
          <Field label="Адрес студии" value={row.studio_address} />
          <Field label="Дата открытия" value={fmtDate(row.studio_open_date)} />
          <Field label="ИНН франчайзи" value={row.partner_inn} />
        </div>
        <div className="mt-2 text-[11px] text-muted-foreground">
          Здесь и в остальном документном контуре печатается юридическое ФИО. Форма имени,
          под которой человека видно в списках и рейтингах, задаётся отдельно на «Оригиналах».
        </div>
      </Card>

      {legalNameOpen && row.partner_number != null && (
        <LegalNameChangeModal
          partnerNumber={Number(row.partner_number)}
          contractId={cid}
          onClose={() => setLegalNameOpen(false)}
          onDone={load}
        />
      )}
    </div>
  )
}

function DocumentRow({
  contractId,
  docType,
  doc,
  isAdvanced,
  onChanged,
  onError,
}: {
  contractId: number
  docType: DocumentType
  doc: StudioDocument | null
  isAdvanced: boolean
  onChanged: () => void
  onError: (msg: string | null) => void
}) {
  const [editing, setEditing] = useState(false)
  const [url, setUrl] = useState(doc?.url ?? '')
  const [note, setNote] = useState(doc?.note ?? '')
  const [readiness, setReadiness] = useState<DocReadiness | ''>(doc?.readiness_status ?? '')
  const [busy, setBusy] = useState(false)
  const [rowError, setRowError] = useState<string | null>(null)

  const startEdit = () => {
    setRowError(null)
    onError(null)
    setUrl(doc?.url ?? '')
    setNote(doc?.note ?? '')
    setReadiness(doc?.readiness_status ?? '')
    setEditing(true)
  }

  const save = async () => {
    setBusy(true)
    setRowError(null)
    onError(null)
    try {
      await upsertStudioDocument({
        contractId,
        docType,
        url: url.trim() || null,
        note: note.trim() || null,
        readiness: readiness || null,
      })
      setEditing(false)
      onChanged()
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      setRowError(message)
      onError(message)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!doc) return
    setBusy(true)
    setRowError(null)
    onError(null)
    try {
      await deleteStudioDocument(doc.document_id)
      setEditing(false)
      onChanged()
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      setRowError(message)
      onError(message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`border-b last:border-0 py-2 ${rowError ? 'border-l-4 border-l-destructive bg-destructive/5 px-2' : 'border-border'}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium">{DOCUMENT_TYPE_LABELS[docType]}</div>
        <div className="flex items-center gap-2">
          {doc?.readiness_status && (
            <Badge variant={doc.readiness_status === 'VERIFIED' ? 'success' : doc.readiness_status === 'READY' ? 'warning' : 'neutral'}>
              {DOC_READINESS_LABELS[doc.readiness_status]}
            </Badge>
          )}
          {isAdvanced && !editing && (
            <Button variant="outline" size="sm" onClick={startEdit}>
              {doc ? 'Изменить' : 'Добавить'}
            </Button>
          )}
        </div>
      </div>

      {!editing && (
        <div className="text-sm mt-1">
          {doc?.url ? (
            <a
              href={doc.url}
              target="_blank"
              rel="noreferrer"
              className="text-primary inline-flex items-center gap-1 hover:underline break-all"
            >
              <ExternalLink size={13} /> {doc.url}
            </a>
          ) : doc?.note ? (
            <span className="text-muted-foreground italic">{doc.note}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
      )}

      {editing && (
        <div className="mt-2 space-y-2">
          {rowError && (
            <LegalErrorAlert className="text-xs">
              {rowError}
            </LegalErrorAlert>
          )}
          <input
            className={inputCls}
            value={url}
            placeholder="Ссылка (URL)"
            onChange={e => setUrl(e.target.value)}
          />
          <input
            className={inputCls}
            value={note}
            placeholder="Заметка вместо ссылки (напр. «у франчайзи»)"
            onChange={e => setNote(e.target.value)}
          />
          <select
            className={inputCls}
            value={readiness}
            onChange={e => setReadiness(e.target.value as DocReadiness | '')}
          >
            <option value="">Статус готовности: —</option>
            {(Object.keys(DOC_READINESS_LABELS) as DocReadiness[]).map(k => (
              <option key={k} value={k}>{DOC_READINESS_LABELS[k]}</option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={busy}>
              <Save size={16} className="mr-1" /> Сохранить
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={busy}>
              Отмена
            </Button>
            {doc && (
              <Button variant="outline" size="sm" onClick={remove} disabled={busy}>
                <Trash2 size={16} className="mr-1" /> Удалить
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
